/**
 * Truy cập module `van-ban` (`/api/v1/m/van-ban/*` qua Gateway, ADR-0073): danh sách, vai trò các cấp;
 * mẫu văn bản; cấu hình đầu vào và webhook. Chọn API thật hay dữ liệu mẫu bằng `dungApiThat()` lúc gọi.
 *
 * Bản giả giữ dữ liệu trong bộ nhớ và kiểm lại vài luật của backend (thiếu webhook URL thì không gửi
 * thử, đơn vị trực thuộc không tồn tại), để view chạy đúng luồng lỗi khi không có backend.
 */
import { dungApiThat, goiApi } from '../api/http';
import type { ApiError } from '../errors';
import {
  MOCK_CAU_VAN_BAN as CAU,
  MOCK_DON_VI,
  MOCK_MAU_VAN_BAN,
  MOCK_NHAT_KY_WEBHOOK,
  MOCK_TICH_HOP,
} from '../../mock/van-ban';
import { mockRequest, mockState } from '../../mock/transport';
import { thayBangNhan } from '../../modules/xu-ly-van-ban/nghiep-vu/mau-van-ban';
import type {
  DonVi,
  GhiDonVi,
  GhiMau,
  GhiTichHop,
  KetQuaGuiThu,
  KetQuaXemTruoc,
  MauVanBan,
  TichHop,
  TichHopKemKhoa,
  TrangDonVi,
  TrangMau,
  TrangNhatKy,
} from '../../modules/xu-ly-van-ban/types';

const GOC = '/api/v1/m/van-ban';
/** Màn cấu hình đọc một lần tối đa 100 dòng rồi lọc ở client, như bản cũ. */
const MOT_TRANG = { page: 1, page_size: 100 };

const khongThay = (message: string): ApiError => ({ code: 'NOT_FOUND', status: 404, message });
const tuChoi = <T>(loi: ApiError): Promise<T> => mockRequest<null>(null).then(() => Promise.reject(loi));
const bayGio = () => new Date().toISOString();
const idMoi = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `mock-${Date.now()}`;

// ─── Bản giả ─────────────────────────────────────────────────────────────────

let donVi: DonVi[] = structuredClone(MOCK_DON_VI);
let mau: MauVanBan[] = structuredClone(MOCK_MAU_VAN_BAN);
let tichHop: TichHop[] = structuredClone(MOCK_TICH_HOP);
let nhatKy = structuredClone(MOCK_NHAT_KY_WEBHOOK);

function voiTenCapTren(ds: DonVi[]): DonVi[] {
  return ds.map((d) => ({ ...d, parent_name: ds.find((p) => p.id === d.parent_id)?.name ?? null }));
}

function trangDonViMock(): Promise<TrangDonVi> {
  const ds = mockState() === 'filtered-empty' ? [] : voiTenCapTren(donVi);
  const dem = (t: DonVi['type']) => donVi.filter((d) => d.type === t).length;
  const rong: TrangDonVi = {
    stats: { total: 0, co_quan: 0, phong_ban: 0, ca_nhan: 0 },
    data: [],
    total: 0,
    page: 1,
    page_size: 100,
    total_pages: 1,
  };
  return mockRequest(
    {
      stats: { total: donVi.length, co_quan: dem('CO_QUAN'), phong_ban: dem('PHONG_BAN'), ca_nhan: dem('CA_NHAN') },
      data: ds,
      total: ds.length,
      page: 1,
      page_size: 100,
      total_pages: 1,
    },
    rong,
  );
}

function trangMauMock(): Promise<TrangMau> {
  const ds = mockState() === 'filtered-empty' ? [] : mau;
  const rong: TrangMau = {
    stats: { total: 0, nghi_dinh_30: 0, tuy_chinh: 0, dang_dung: 0 },
    data: [],
    total: 0,
    page: 1,
    page_size: 100,
    total_pages: 1,
  };
  return mockRequest(
    {
      stats: {
        total: mau.length,
        nghi_dinh_30: mau.filter((m) => m.source === 'NGHI_DINH_30').length,
        tuy_chinh: mau.filter((m) => m.source === 'TUY_CHINH').length,
        dang_dung: mau.filter((m) => m.status === 'DANG_DUNG').length,
      },
      data: [...ds].sort((a, b) => b.created_at.localeCompare(a.created_at)),
      total: ds.length,
      page: 1,
      page_size: 100,
      total_pages: 1,
    },
    rong,
  );
}

function ghiMauMock(cu: MauVanBan | null, vao: GhiMau): MauVanBan {
  const body = vao.body_template ?? cu?.body_template ?? null;
  return {
    ...(cu ?? {
      id: idMoi(),
      source: 'TUY_CHINH',
      source_label: CAU.tuyChinh,
      structure: null,
      template_content: null,
      original_filename: null,
      preview_content: null,
      created_at: bayGio(),
    }),
    name: vao.name,
    symbol_code: vao.symbol_code,
    status: vao.status,
    status_label: vao.status === 'DANG_DUNG' ? CAU.dangDung : CAU.ngungDung,
    description: vao.description ?? cu?.description ?? null,
    fields: vao.fields ?? cu?.fields ?? null,
    body_template: body,
    default_meta: vao.default_meta ?? cu?.default_meta ?? null,
    compose_mode: body ? 'FIELD_FILL' : 'AI_GENERATE',
    updated_at: bayGio(),
  } as MauVanBan;
}

const banGia = {
  donVi: {
    danhSach: trangDonViMock,
    tao: (vao: GhiDonVi): Promise<DonVi> => {
      if (vao.parent_id && !donVi.some((d) => d.id === vao.parent_id)) {
        return tuChoi({ code: 'INVALID_ARGUMENT', status: 400, message: CAU.capTrenKhongCo });
      }
      const moi: DonVi = {
        id: idMoi(),
        ...vao,
        score: vao.score ?? 8,
        supervisor: vao.type === 'CA_NHAN' ? null : vao.supervisor,
        parent_name: null,
        created_at: bayGio(),
        updated_at: bayGio(),
      };
      donVi = [...donVi, moi];
      return mockRequest(moi);
    },
    sua: (id: string, vao: GhiDonVi): Promise<DonVi> => {
      const cu = donVi.find((d) => d.id === id);
      if (!cu) return tuChoi(khongThay(CAU.khongThayDonVi));
      const moi: DonVi = {
        ...cu,
        ...vao,
        score: vao.score ?? cu.score,
        supervisor: vao.type === 'CA_NHAN' ? null : vao.supervisor,
        updated_at: bayGio(),
      };
      donVi = donVi.map((d) => (d.id === id ? moi : d));
      return mockRequest(moi);
    },
    xoa: (id: string): Promise<void> => {
      donVi = donVi.filter((d) => d.id !== id).map((d) => (d.parent_id === id ? { ...d, parent_id: null } : d));
      return mockRequest(undefined);
    },
  },
  mau: {
    danhSach: trangMauMock,
    khaNang: () => mockRequest({ ai_preview: true }),
    tao: (vao: GhiMau): Promise<MauVanBan> => {
      const moi = ghiMauMock(null, vao);
      mau = [moi, ...mau];
      return mockRequest(moi);
    },
    sua: (id: string, vao: GhiMau): Promise<MauVanBan> => {
      const cu = mau.find((m) => m.id === id);
      if (!cu) return tuChoi(khongThay(CAU.khongThayMau));
      const moi = ghiMauMock(cu, vao);
      mau = mau.map((m) => (m.id === id ? moi : m));
      return mockRequest(moi);
    },
    xemTruoc: (id: string): Promise<KetQuaXemTruoc> => {
      const cu = mau.find((m) => m.id === id);
      if (!cu) return tuChoi(khongThay(CAU.khongThayMau));
      const preview = cu.body_template
        ? thayBangNhan(cu.body_template, cu.fields)
        : `${cu.name.toUpperCase()}\n\n${CAU.xemTruocAi}`;
      mau = mau.map((m) => (m.id === id ? { ...m, preview_content: preview } : m));
      return mockRequest({ id, preview_content: preview, message: CAU.daTaoXemTruoc });
    },
    xoa: (id: string): Promise<void> => {
      mau = mau.filter((m) => m.id !== id);
      return mockRequest(undefined);
    },
  },
  tichHop: {
    danhSach: (): Promise<TichHop[]> =>
      mockRequest(mockState() === 'filtered-empty' ? [] : [...tichHop], []),
    tao: (vao: GhiTichHop): Promise<TichHopKemKhoa> => {
      const { webhook_token: _bo, ...giu } = vao;
      const moi: TichHop = {
        id: idMoi(),
        ...giu,
        api_key_prefix: 'fxai_Mock1234',
        is_active: true,
        created_at: bayGio(),
        updated_at: bayGio(),
      };
      tichHop = [moi, ...tichHop];
      return mockRequest({ ...moi, api_key: 'fxai_Mock1234-khoa-gia-chi-dung-o-ban-du-lieu-mau' });
    },
    sua: (id: string, vao: GhiTichHop): Promise<TichHop> => {
      const cu = tichHop.find((x) => x.id === id);
      if (!cu) return tuChoi(khongThay(CAU.khongThayTichHop));
      const { webhook_token: _bo, ...giu } = vao;
      const moi: TichHop = { ...cu, ...giu, is_active: vao.is_active ?? cu.is_active, updated_at: bayGio() };
      tichHop = tichHop.map((x) => (x.id === id ? moi : x));
      return mockRequest(moi);
    },
    capLaiKhoa: (id: string): Promise<TichHopKemKhoa> => {
      const cu = tichHop.find((x) => x.id === id);
      if (!cu) return tuChoi(khongThay(CAU.khongThayTichHop));
      const moi = { ...cu, api_key_prefix: 'fxai_Moi98765', updated_at: bayGio() };
      tichHop = tichHop.map((x) => (x.id === id ? moi : x));
      return mockRequest({ ...moi, api_key: 'fxai_Moi98765-khoa-moi-chi-dung-o-ban-du-lieu-mau' });
    },
    guiThu: (id: string): Promise<KetQuaGuiThu> => {
      const cu = tichHop.find((x) => x.id === id);
      if (!cu) return tuChoi(khongThay(CAU.khongThayTichHop));
      if (!cu.webhook_url) {
        return tuChoi({ code: 'INVALID_ARGUMENT', status: 400, message: CAU.chuaCoWebhook });
      }
      nhatKy = [
        {
          id: idMoi(),
          integration_config_id: id,
          summarization_id: null,
          trigger: 'TEST',
          attempt: 1,
          status: 'SUCCESS',
          http_status: 200,
          response_body: '{"ok":true}',
          error_message: null,
          created_at: bayGio(),
        },
        ...nhatKy,
      ];
      return mockRequest({ success: true, http_status: 200, response_body: '{"ok":true}', error: null, sent_payload: { test: true } });
    },
    nhatKy: (id: string): Promise<TrangNhatKy> => {
      const ds = nhatKy.filter((l) => l.integration_config_id === id);
      return mockRequest({ data: ds, total: ds.length, page: 1, page_size: 50, total_pages: 1 });
    },
    xoa: (id: string): Promise<void> => {
      tichHop = tichHop.filter((x) => x.id !== id);
      nhatKy = nhatKy.filter((l) => l.integration_config_id !== id);
      return mockRequest(undefined);
    },
  },
};

// ─── Bản thật ────────────────────────────────────────────────────────────────

const ma = (id: string) => encodeURIComponent(id);

const banThat: typeof banGia = {
  donVi: {
    danhSach: () => goiApi<TrangDonVi>(`${GOC}/processing-units`, { query: MOT_TRANG }),
    tao: (vao) => goiApi<DonVi>(`${GOC}/processing-units`, { method: 'POST', body: vao }),
    sua: (id, vao) => goiApi<DonVi>(`${GOC}/processing-units/${ma(id)}`, { method: 'PATCH', body: vao }),
    xoa: (id) => goiApi<void>(`${GOC}/processing-units/${ma(id)}`, { method: 'DELETE' }),
  },
  mau: {
    danhSach: () => goiApi<TrangMau>(`${GOC}/document-templates`, { query: MOT_TRANG }),
    khaNang: () => goiApi<{ ai_preview: boolean }>(`${GOC}/document-templates/capabilities`),
    tao: (vao) => goiApi<MauVanBan>(`${GOC}/document-templates`, { method: 'POST', body: vao }),
    sua: (id, vao) => goiApi<MauVanBan>(`${GOC}/document-templates/${ma(id)}`, { method: 'PATCH', body: vao }),
    xemTruoc: (id) =>
      goiApi<KetQuaXemTruoc>(`${GOC}/document-templates/${ma(id)}/generate-preview`, { method: 'POST' }),
    xoa: (id) => goiApi<void>(`${GOC}/document-templates/${ma(id)}`, { method: 'DELETE' }),
  },
  tichHop: {
    danhSach: () => goiApi<TichHop[]>(`${GOC}/integrations`),
    tao: (vao) => goiApi<TichHopKemKhoa>(`${GOC}/integrations`, { method: 'POST', body: vao }),
    sua: (id, vao) => goiApi<TichHop>(`${GOC}/integrations/${ma(id)}`, { method: 'PATCH', body: vao }),
    capLaiKhoa: (id) => goiApi<TichHopKemKhoa>(`${GOC}/integrations/${ma(id)}/rotate-key`, { method: 'POST' }),
    guiThu: (id) => goiApi<KetQuaGuiThu>(`${GOC}/integrations/${ma(id)}/test-webhook`, { method: 'POST' }),
    nhatKy: (id) =>
      goiApi<TrangNhatKy>(`${GOC}/integrations/${ma(id)}/webhook-logs`, { query: { page: 1, page_size: 50 } }),
    xoa: (id) => goiApi<void>(`${GOC}/integrations/${ma(id)}`, { method: 'DELETE' }),
  },
};

const chon = () => (dungApiThat() ? banThat : banGia);

export const vanBanRepository = {
  donVi: {
    danhSach: () => chon().donVi.danhSach(),
    tao: (vao: GhiDonVi) => chon().donVi.tao(vao),
    sua: (id: string, vao: GhiDonVi) => chon().donVi.sua(id, vao),
    xoa: (id: string) => chon().donVi.xoa(id),
  },
  mau: {
    danhSach: () => chon().mau.danhSach(),
    khaNang: () => chon().mau.khaNang(),
    tao: (vao: GhiMau) => chon().mau.tao(vao),
    sua: (id: string, vao: GhiMau) => chon().mau.sua(id, vao),
    xemTruoc: (id: string) => chon().mau.xemTruoc(id),
    xoa: (id: string) => chon().mau.xoa(id),
  },
  tichHop: {
    danhSach: () => chon().tichHop.danhSach(),
    tao: (vao: GhiTichHop) => chon().tichHop.tao(vao),
    sua: (id: string, vao: GhiTichHop) => chon().tichHop.sua(id, vao),
    capLaiKhoa: (id: string) => chon().tichHop.capLaiKhoa(id),
    guiThu: (id: string) => chon().tichHop.guiThu(id),
    nhatKy: (id: string) => chon().tichHop.nhatKy(id),
    xoa: (id: string) => chon().tichHop.xoa(id),
  },
};
